import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { DateTime } from "luxon";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

const PackagePaymentDetails = ({ customerSelectedRow, selectedPackage }) => {
  const mobileSize = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const currentSession = useSelector((state) => state.sessions.currentSession);
  const currentUser = useSelector((state) => state[currentSession].user);
  const [copiedDocKey, setCopiedDocKey] = useState(null);

  const rootRef = useRef(null);
  const measureRef = useRef(null);

  const [charWidthPx, setCharWidthPx] = useState(7);
  const [containerWidthPx, setContainerWidthPx] = useState(0);

  useEffect(() => {
    if (!measureRef.current) return;
    const rect = measureRef.current.getBoundingClientRect();
    if (rect?.width && rect.width > 0) setCharWidthPx(rect.width);
  }, []);

  useEffect(() => {
    if (!rootRef.current || typeof ResizeObserver === "undefined") return;

    const ro = new ResizeObserver((entries) => {
      const entry = entries?.[0];
      const w = entry?.contentRect?.width;
      if (typeof w === "number") setContainerWidthPx(w);
    });

    ro.observe(rootRef.current);
    // init
    setContainerWidthPx(rootRef.current.getBoundingClientRect()?.width || 0);

    return () => ro.disconnect();
  }, []);

  const getCurrNotarization = (key) =>
    selectedPackage?.processing_permit?.notarization?.[key]?.notarization;

  const getPayments = () =>
    selectedPackage?.processing_permit?.notarization?.payments;

  const getRecording = () => selectedPackage?.processing_permit?.recording;

  const getAppointments = (key) => getCurrNotarization(key)?.appointments;

  const getRecordingTotalFees = () => {
    const fees = getRecording()?.fees;
    if (!Array.isArray(fees) || fees.length === 0) return null;

    return fees.reduce((sum, fee) => {
      const parsed = Number(fee?.amount);
      return sum + (Number.isFinite(parsed) ? parsed : 0);
    }, 0);
  };

  const getUserTimezone = () =>
    currentUser?.subuser?.timezone ||
    currentUser?.user?.personal?.[0]?.timezone ||
    currentUser?.user?.personal?.[0]?.timeZone ||
    "America/New_York";

  const formatTimestampToUS = (timestampValue) => {
    if (
      timestampValue === null ||
      timestampValue === undefined ||
      timestampValue === ""
    ) {
      return "N/A";
    }

    const asString = String(timestampValue).trim();
    const isNumeric = asString !== "" && !Number.isNaN(Number(asString));

    const dt = isNumeric
      ? DateTime.fromSeconds(Number(asString), { zone: "utc" })
      : DateTime.fromISO(asString, { zone: "utc" });

    if (!dt.isValid) return "N/A";

    return dt.setZone(getUserTimezone()).toFormat("MM/dd/yyyy hh:mm a");
  };

  const formatAppointmentToUS = (appointment) => {
    if (!appointment || typeof appointment !== "object") return "N/A";

    const year = Number(appointment?.year);
    const month = Number(appointment?.month);
    const day = Number(appointment?.day_of_month);
    const [hourRaw, minuteRaw] = String(appointment?.time || "").split(":");
    const hour = Number(hourRaw);
    const minute = Number(minuteRaw);

    if (
      Number.isFinite(year) &&
      Number.isFinite(month) &&
      Number.isFinite(day) &&
      Number.isFinite(hour) &&
      Number.isFinite(minute)
    ) {
      const dt = DateTime.fromObject(
        { year, month, day, hour, minute },
        { zone: appointment?.timezone || "utc" },
      );

      if (dt.isValid) {
        return dt.setZone(getUserTimezone()).toFormat("MM/dd/yyyy hh:mm a");
      }
    }

    return formatTimestampToUS(appointment?.created_at);
  };

  const getAppointmentBuckets = (key) => {
    const appointments = getAppointments(key) || {};

    return [
      { label: "Booked", items: appointments?.booked || [] },
      { label: "Cancelled", items: appointments?.cancelled || [] },
      { label: "Prebooked", items: appointments?.prebooked || [] },
      { label: "Archived", items: appointments?.archived || [] },
    ];
  };

  const formatDocNameToTitle = (name) => {
    if (!name) return "N/A";

    return String(name)
      .replaceAll("_", " ")
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const formatAmountUSD = (amount) => {
    let parsed = Number(amount);
    if (Number.isNaN(parsed)) return "N/A";
    
    // Divided amount by 100 as amount is coming in cents
    parsed /= 100;

    return parsed.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const capitalizeFirstChar = (value) => {
    if (!value) return value;

    const str = String(value);
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const handleCopyToClipboard = async (value, docKey) => {
    if (!value) return;

    try {
      await navigator.clipboard.writeText(value);
      setCopiedDocKey(docKey);
      setTimeout(() => setCopiedDocKey(null), 900);
    } catch (error) {
      console.error("Failed to copy text:", error);
    }
  };

  const chunkSize = useMemo(() => {
    // Recomputed on resize to prevent scroll returning when viewport shrinks.
    if (!containerWidthPx || !charWidthPx) return 18;
    const approxChars = Math.max(
      12,
      Math.floor(containerWidthPx / charWidthPx) - 12,
    );
    return Math.min(28, approxChars);
  }, [containerWidthPx, charWidthPx]);

  const softWrapUrl = (url, maxChunkChars) => {
    if (!url) return "";
    const s = String(url);

    // Add wrap opportunities after common URL delimiters
    const withDelims = s.replace(/([/?#&=._-])/g, "$1\u200B");

    // Chunk long uninterrupted runs (e.g., JWT/base64 segments)
    const safeChunk = Math.max(10, Number(maxChunkChars) || 18);
    const chunked = withDelims.replace(
      new RegExp(`([^\\u200B]{${safeChunk}})`, "g"),
      "$1\u200B",
    );

    return chunked;
  };

  const urlTextSx = {
    display: "block",
    pl: 2,
    mt: 0.25,
    cursor: "pointer",
    color: "grey",
    maxWidth: "100%",
    minWidth: 0,
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    wordBreak: "break-all", // strongest: prevents scroll on extreme strings
    lineHeight: 1.3,
  };

  const recordingTotalFees = getRecordingTotalFees();

  const packageAccordionHeaderContent = (display) => (
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{
        overflowWrap: "anywhere",
        wordBreak: "break-word",
        display,
        mb: mobileSize ? 1 : 0,
      }}
    >
      {selectedPackage?.id} •{" "}
      {selectedPackage?.processing_permit?.notarization?.notarizing_docs === 0
        ? "All Documents"
        : "NOC Only"}{" "}
      •{" "}
      {capitalizeFirstChar(
        selectedPackage?.processing_permit?.notarization?.signing_order,
      )}{" "}
      {selectedPackage?.processing_permit?.notarization?.signing_order
        ? "First"
        : ""}
    </Typography>
  );

  const ownerAccordionHeaderContent = (key, display) => (
    <Box
      sx={{
        display,
        gap: 1,
        flexWrap: "wrap",
        justifyContent: { xs: "flex-start", sm: "flex-end" },
        flex: "1 1 260px",
        minWidth: 0,
        mb: mobileSize ? 1 : 0,
      }}
    >
      {[
        {
          k: "Notarized",
          v: getCurrNotarization(key)?.only_sign
            ? "N/A"
            : getCurrNotarization(key)?.is_notarized
              ? "Yes"
              : "No",
        },
        {
          k: "Signed",
          v: !getCurrNotarization(key)?.only_sign
            ? "N/A"
            : getCurrNotarization(key)?.is_signed
              ? "Yes"
              : "No",
        },
        {
          k: "Has Notary",
          v: getCurrNotarization(key)?.has_notary ? "Yes" : "No",
        },
      ].map((x) => (
        <Box
          key={`${key}-${x.k}`}
          sx={{
            px: 1,
            py: 0.5,
            borderRadius: 999,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.default",
            minWidth: 0,
          }}
        >
          <Typography
            variant="caption"
            fontWeight={800}
            color="primary"
            sx={{ overflowWrap: "anywhere" }}
          >
            {x.k}:{" "}
            <Box component="span" sx={{ fontWeight: 900, color: "grey" }}>
              {x.v}
            </Box>
          </Typography>
        </Box>
      ))}
    </Box>
  );

  return (
    <Box
      ref={rootRef}
      sx={{
        width: "100%",
        minWidth: 0,
        overflowX: "hidden", // never allow horizontal scroll; wrap instead
      }}
    >
      {/* hidden measurer for approx monospace character width */}
      <Box
        ref={measureRef}
        sx={{
          position: "absolute",
          visibility: "hidden",
          pointerEvents: "none",
          fontFamily: "monospace",
          fontSize: 12,
        }}
      >
        M
      </Box>

      <Accordion
        defaultExpanded={false}
        disableGutters
        elevation={0}
        sx={{
          width: "100%",
          minWidth: 0,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          "&:before": { display: "none" },
          overflow: "hidden",
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            px: 2,
            py: 1.5,
            "& .MuiAccordionSummary-content": { margin: 0, minWidth: 0 },
          }}
        >
          <Box sx={{ width: "100%", minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              fontWeight={900}
              color="primary"
              sx={{ lineHeight: 1.2 }}
            >
              Package Notarization / eSign / Recording Details
            </Typography>

            {packageAccordionHeaderContent(
              mobileSize ? "none" : "inline-block",
            )}
          </Box>
        </AccordionSummary>

        <AccordionDetails
          sx={{
            p: 2,
            minWidth: 0,
            overflowX: "hidden",
          }}
        >
          {packageAccordionHeaderContent(mobileSize ? "inline-block" : "none")}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              minWidth: 0,
            }}
          >
            {[
              { title: "Owner", key: "owner" },
              { title: "Contractor", key: "contractor" },
            ].map(({ title, key }) => (
              <Accordion
                key={key}
                defaultExpanded={false}
                disableGutters
                elevation={0}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  "&:before": { display: "none" },
                  overflow: "hidden",
                  bgcolor: "background.paper",
                  minWidth: 0,
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    px: 2,
                    py: 1.25,
                    "& .MuiAccordionSummary-content": {
                      margin: 0,
                      minWidth: 0,
                    },
                  }}
                >
                  <Box sx={{ width: "100%", minWidth: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 2,
                        alignItems: "flex-start",
                        minWidth: 0,
                        flexWrap: "wrap",
                      }}
                    >
                      <Box sx={{ minWidth: 0, flex: "1 1 240px" }}>
                        <Typography
                          variant="subtitle1"
                          fontWeight={900}
                          color="primary"
                          sx={{
                            overflowWrap: "anywhere",
                            wordBreak: "break-word",
                            minWidth: 0,
                          }}
                        >
                          {title}
                        </Typography>

                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ overflowWrap: "anywhere" }}
                        >
                          Notarization Type:{" "}
                          <Box component="span" sx={{ fontWeight: 900 }}>
                            {getCurrNotarization(key)?.is_ipen
                              ? "IPEN"
                              : getCurrNotarization(key)?.only_sign
                                ? "eSIGN"
                                : "RON"}
                          </Box>
                        </Typography>
                      </Box>

                      {ownerAccordionHeaderContent(
                        key,
                        mobileSize ? "none" : "flex",
                      )}
                    </Box>
                  </Box>
                </AccordionSummary>

                <AccordionDetails
                  sx={{ p: 2, minWidth: 0, overflowX: "hidden" }}
                >
                  {ownerAccordionHeaderContent(
                    key,
                    mobileSize ? "flex" : "none",
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.25,
                      minWidth: 0,
                    }}
                  >
                    {/* Status */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                        minWidth: 0,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 2,
                          borderBottom: "1px dashed",
                          borderColor: "divider",
                          pb: 0.5,
                          minWidth: 0,
                          flexWrap: "wrap",
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="primary"
                          fontWeight="bold"
                        >
                          Completed At
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={900}
                          sx={{
                            textAlign: { xs: "left", sm: "right" },
                            overflowWrap: "anywhere",
                            wordBreak: "break-word",
                            color: "grey",
                            minWidth: 0,
                          }}
                        >
                          {formatTimestampToUS(
                            getCurrNotarization(key)?.completed_at,
                          )}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Selected Documents */}
                    <Box sx={{ mt: 1, minWidth: 0 }}>
                      <Typography
                        variant="subtitle2"
                        color="primary"
                        fontWeight={900}
                        sx={{ mb: 0.5 }}
                      >
                        Selected Documents
                      </Typography>

                      {getCurrNotarization(key)?.selected_documents?.map(
                        (doc, i) => (
                          <Box
                            key={`${key}-doc-${i}`}
                            sx={{ pl: 1.25, mt: 0.5, minWidth: 0 }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                overflowWrap: "anywhere",
                                wordBreak: "break-word",
                                color: "grey",
                                fontWeight: "bold",
                                minWidth: 0,
                              }}
                            >
                              • {formatDocNameToTitle(doc?.name)}
                            </Typography>

                            {doc?.pdfUrl && (
                              <Tooltip
                                title="Copied"
                                placement="top"
                                open={copiedDocKey === `${key}-doc-${i}`}
                                disableFocusListener
                                disableHoverListener
                                disableTouchListener
                              >
                                <Typography
                                  variant="caption"
                                  onClick={() =>
                                    handleCopyToClipboard(
                                      doc.pdfUrl,
                                      `${key}-doc-${i}`,
                                    )
                                  }
                                  sx={urlTextSx}
                                >
                                  {softWrapUrl(doc.pdfUrl, chunkSize)}
                                </Typography>
                              </Tooltip>
                            )}
                          </Box>
                        ),
                      )}

                      {(!getCurrNotarization(key)?.selected_documents ||
                        getCurrNotarization(key)?.selected_documents?.length ===
                          0) && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ pl: 1.25, display: "block", mt: 0.5 }}
                        >
                          No documents selected
                        </Typography>
                      )}
                    </Box>

                    {/* RECORDING */}
                    <Box sx={{ mt: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 2,
                          alignItems: "baseline",
                          flexWrap: "wrap",
                          minWidth: 0,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          fontWeight={900}
                          color="primary"
                        >
                          Recording
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ overflowWrap: "anywhere" }}
                        >
                          Total Cost (PR):{" "}
                          <Box component="span" sx={{ fontWeight: 900 }}>
                            {recordingTotalFees !== null
                              ? formatAmountUSD(recordingTotalFees)
                              : "N/A"}
                          </Box>
                        </Typography>
                      </Box>

                      {[
                        {
                          label: "Recorded",
                          value: getRecording()?.is_recorded ? "Yes" : "No",
                        },
                        {
                          label: "In Progress",
                          value: getRecording()?.in_progress ? "Yes" : "No",
                        },
                        {
                          label: "Auto Record",
                          value: getRecording()?.auto_record ? "Yes" : "No",
                        },
                        {
                          label: "Status",
                          value: getRecording()?.status,
                        },
                        {
                          label: "Started At",
                          value: getRecording()?.started_at,
                        },
                        {
                          label: "Completed At",
                          value: getRecording()?.finished_at,
                        },
                      ].map((p) => (
                        <Box
                          key={`${key}-pay-${p.label}`}
                          sx={{ mt: 0.75, pl: 1.25, minWidth: 0 }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              overflowWrap: "anywhere",
                              color: "grey",
                              fontWeight: "bold",
                              minWidth: 0,
                            }}
                          >
                            • {p.label}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              pl: 2,
                              overflowWrap: "anywhere",
                              wordBreak: "break-word",
                              color: "grey",
                              minWidth: 0,
                              display: "block",
                            }}
                          >
                            {p.label === "Started At" ||
                            p.label === "Completed At"
                              ? formatTimestampToUS(p.value)
                              : (p.value ?? "N/A")}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    {/* Payments */}
                    <Box sx={{ mt: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 2,
                          alignItems: "baseline",
                          flexWrap: "wrap",
                          minWidth: 0,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          fontWeight={900}
                          color="primary"
                        >
                          Payments
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ overflowWrap: "anywhere" }}
                        >
                          Total Paid:{" "}
                          <Box component="span" sx={{ fontWeight: 900 }}>
                            {getPayments()?.paid
                              ? formatAmountUSD(getPayments()?.amount)
                              : "N/A"}
                          </Box>
                        </Typography>
                      </Box>

                      {[
                        {
                          label: "Invoice #",
                          value: getPayments()?.invoice_num,
                        },
                        {
                          label: "Customer ID",
                          value: getPayments()?.customer_id,
                        },
                        { label: "Price ID", value: getPayments()?.price_id },
                      ].map((p) => (
                        <Box
                          key={`${key}-pay-${p.label}`}
                          sx={{ mt: 0.75, pl: 1.25, minWidth: 0 }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              overflowWrap: "anywhere",
                              color: "grey",
                              fontWeight: "bold",
                              minWidth: 0,
                            }}
                          >
                            • {p.label}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              pl: 2,
                              overflowWrap: "anywhere",
                              wordBreak: "break-word",
                              color: "grey",
                              minWidth: 0,
                              display: "block",
                            }}
                          >
                            {p.value ?? "N/A"}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    {/* BN Session Links */}
                    <Box sx={{ mt: 1, minWidth: 0 }}>
                      <Typography
                        variant="subtitle2"
                        fontWeight={900}
                        color="primary"
                        sx={{ mb: 0.5 }}
                      >
                        BN Session Links
                      </Typography>

                      {[
                        {
                          k: "Notarization ID",
                          v: selectedPackage?.processing_permit?.notarization
                            ?.notarization_id,
                          isUrl: false,
                        },
                        {
                          k: "Blue Notary ID",
                          v: getCurrNotarization(key)?.bn_id,
                          isUrl: false,
                        },
                        {
                          k: "Signer URL",
                          v: getCurrNotarization(key)?.bn_session?.signer_url,
                          isUrl: true,
                        },
                        {
                          k: "Notary URL",
                          v: getCurrNotarization(key)?.bn_session?.notary_url,
                          isUrl: true,
                        },
                      ].map((row) => (
                        <Box
                          key={`${key}-link-${row.k}`}
                          sx={{ pl: 1.25, mt: 0.5, minWidth: 0 }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ color: "grey", fontWeight: "bold" }}
                          >
                            • {row.k}
                          </Typography>

                          {row.isUrl && row.v ? (
                            <Tooltip
                              title="Copied"
                              placement="top"
                              open={copiedDocKey === `${key}-bn-${row.k}`}
                              disableFocusListener
                              disableHoverListener
                              disableTouchListener
                            >
                              <Typography
                                variant="caption"
                                onClick={() =>
                                  handleCopyToClipboard(
                                    row.v,
                                    `${key}-bn-${row.k}`,
                                  )
                                }
                                sx={urlTextSx}
                              >
                                {softWrapUrl(row.v, chunkSize)}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography
                              variant="caption"
                              sx={{
                                pl: 2,
                                color: "grey",
                                overflowWrap: "anywhere",
                                wordBreak: "break-word",
                                minWidth: 0,
                                display: "block",
                              }}
                            >
                              {row.v ?? "N/A"}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>

                    {/* Notary Details */}
                    <Box sx={{ mt: 1, minWidth: 0 }}>
                      <Typography
                        variant="subtitle2"
                        fontWeight={900}
                        sx={{ mb: 0.5 }}
                        color="primary"
                      >
                        Notary Details
                      </Typography>

                      {[
                        {
                          k: "Notary Name",
                          v: formatDocNameToTitle(
                            getCurrNotarization(key)?.notary?.notary_name,
                          ),
                        },
                        {
                          k: "Notary ID",
                          v:
                            getCurrNotarization(key)?.notary?.notary_id ||
                            "N/A",
                        },
                        {
                          k: "Notary Accepted At",
                          v: formatTimestampToUS(
                            getCurrNotarization(key)?.notary
                              ?.assigned_notary_at,
                          ),
                        },
                        {
                          k: "Notary Failed",
                          v: getCurrNotarization(key)?.notary_failed
                            ? "Yes"
                            : "No",
                        },
                        {
                          k: "Failure Count",
                          v: getCurrNotarization(key)?.attempt || "0",
                        },
                      ].map((row) => (
                        <Box
                          key={`${key}-notary-${row.k}`}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            columnGap: mobileSize ? 1 : 2,
                            pl: 1.25,
                            minWidth: 0,
                            flexWrap: "wrap",
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="grey"
                            sx={{ fontWeight: "bold" }}
                          >
                            {row.k}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              textAlign: { xs: "left", sm: "right" },
                              overflowWrap: "anywhere",
                              wordBreak: "break-word",
                              minWidth: 0,
                              color: "grey",
                            }}
                          >
                            {row.v}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    {/* Appointments */}
                    <Box sx={{ mt: 1, minWidth: 0 }}>
                      <Typography
                        variant="subtitle2"
                        fontWeight={900}
                        sx={{ mb: 0.5 }}
                        color="primary"
                      >
                        Appointments
                      </Typography>

                      {getAppointmentBuckets(key).map((row) => (
                        <Box
                          key={`${key}-appt-${row.label}`}
                          sx={{
                            pl: 1.25,
                            minWidth: 0,
                            mt: 0.5,
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ color: "grey", fontWeight: "bold" }}
                          >
                            • {row.label} ({row.items?.length || 0})
                          </Typography>

                          {Array.isArray(row.items) && row.items.length > 0 ? (
                            row.items.map((appointment, idx) => (
                              <Typography
                                key={`${key}-appt-${row.label}-${idx}`}
                                variant="caption"
                                sx={{
                                  pl: 2,
                                  color: "grey",
                                  overflowWrap: "anywhere",
                                  wordBreak: "break-word",
                                  minWidth: 0,
                                  display: "block",
                                }}
                              >
                                {formatAppointmentToUS(appointment)}
                              </Typography>
                            ))
                          ) : (
                            <Typography
                              variant="caption"
                              sx={{
                                pl: 2,
                                color: "grey",
                                overflowWrap: "anywhere",
                                wordBreak: "break-word",
                                minWidth: 0,
                                display: "block",
                              }}
                            >
                              None
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default PackagePaymentDetails;
